import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { usePermission, useScope } from '../../context/PermissionContext';
import { STAFF_ROLES } from '../../constants/staff';
import useProcedureStore from '../../stores/procedureStore';
import { procedurePickerDeptAnchor } from './departmentResolution';
import { listOpenLabItems } from '../../services/api/labApi';
import {
  advisedAwaitingAssignment,
  groupAdvisedByTest,
  addableLabCatalog,
  buildInvestigationLines,
} from './labInvestigationLines';

/**
 * RegistrationInvestigationsSection — the Lab-Visit counterpart of
 * RegistrationProceduresSection. A walk-in Lab Visit is doctor-less, so instead
 * of procedures the front desk picks the *investigations* (lab tests) the
 * patient came for.
 *
 * Two sources, mirroring how RegistrationProceduresSection works — OPD only
 * (no admissionId); see the C1 note below for IPD:
 *   1. DOCTOR-ADVISED tests (top) — the patient's open lab orders a doctor
 *      already advised but that aren't assigned yet. Shown with the advising
 *      doctor's name and selectable even when the test is NOT receptionist-
 *      orderable (e.g. Ultrasound, NT scan) — a doctor having advised it IS the
 *      authority. Selecting one ASSIGNS the EXISTING order to the visit's
 *      technician (no duplicate order; no re-charge if already paid).
 *   2. CATALOG tests — receptionist-allowed lab tests the front desk may order
 *      on its own. Selecting one ADVISES a fresh order. The catalog dropdown
 *      EXCLUDES anything already advised above, so a test can't be duplicated.
 *      FIX-4 (LAB-ASSIGN-2) — a LAB_TECHNICIAN caller gets the FULL active
 *      in-house lab catalog here instead of only the receptionist-allowed
 *      subset (owner ruling: trusted like a doctor advising).
 *
 * IPD (admissionId present) — the catalog dropdown does NOT exclude a test
 * that's already advised: a stay spans many days, and the SAME investigation
 * (e.g. a repeat CT Head on day 1 and day 4) must be re-orderable, each pick
 * minting its own fresh, separately-billable order.
 *
 * C1 (2026-08-23) — the doctor-advised block (source 1 above) never renders
 * in an admission context at all. An accounting-only organisation never
 * assigns a technician, so `advisedAwaitingAssignment` never lets a row
 * leave — it would accumulate for the whole stay, all tickable, while ticking
 * one could never place an order there (the submit drops `source:'advised'`
 * lines for an admission — OrderLabDialog.jsx). The stay's real lab ledger is
 * the Diagnostics tab. OPD (no admissionId) keeps the block and the dedupe —
 * one visit, one workflow per test, and ticking genuinely assigns.
 *
 * Emits `onChange(lines)` where each line carries a `source` discriminator the
 * registration submit consumes:
 *   { source: 'advised', lab_order_id, procedure_id, name, cost, skip_payment, paid }
 *   { source: 'new', procedure_id, name, cost, skip_payment, paid:false, quantity }
 *
 * W6/C5 — a qty stepper on catalog (NEW) rows ONLY (default 1, min 1, integer;
 * mirrors RegistrationProceduresSection's W2 stepper exactly). Doctor-advised
 * rows keep NO stepper — they stay qty=1 per owner ruling (registration
 * picker only; advise flow untouched).
 *
 * L1 (lab-credit) — additive `admissionId`/`ipNumber`: when the caller passes
 * an admission context (the admission lab-attach surface), every NEW-catalog
 * row shows an "On credit (settle later)" / "Collect payment now" choice,
 * defaulting to **On credit** (owner ruling D2 — IP defaults ON, editable).
 * Doctor-advised rows never show it (no money set by advice, AC-NEG). A
 * zero-cost/no-charge (`skip_payment`) catalog row never shows it either —
 * nothing to settle. Both props are omitted by every existing OPD walk-in
 * caller — the section renders byte-identical (no toggle, no admission
 * stamp) when they're absent.
 *
 * "Collect payment now" is gated on the Settle-card's own permission
 * (`COLLECT_PAYMENT`) — a desk user who can't settle credit doesn't see the
 * option and can only place the row on credit. "On credit" carries no such
 * gate (unchanged).
 */
export default function RegistrationInvestigationsSection({ patientId, departmentId = '', admissionId = null, ipNumber = null, onChange }) {
  const { isDark } = useTheme();
  const canCollectNow = usePermission('COLLECT_PAYMENT');
  // LAB-ASSIGN-2 — a LAB_TECHNICIAN caller. Two knock-on effects below:
  //  (FE-3, Q-B2 no self-claim) the doctor-advised block would tick an ASSIGN
  //  call the role gets 403'd on (self-claim goes through the technician's
  //  own queue, not this picker) — swapped for a READ-ONLY informational line
  //  for this role (FIX-3 item 1: it must never be silently absent), every
  //  other role keeps the tickable block; (FE-4) an IPD catalog row is
  //  credit-fixed for this role — the interactive credit/collect-now chooser
  //  becomes a plain static line.
  const { role } = useScope();
  const isLabTechnicianCaller = (role || '').toUpperCase() === STAFF_ROLES.LAB_TECHNICIAN;
  // Full admin catalog — used to RESOLVE names/flags of already-picked catalog
  // rows (mirrors RegistrationProceduresSection's `catalogById`; A4/S3 rule).
  const { procedures, fetchProcedures } = useProcedureStore();
  // A5 — the "+ Add Investigation…" picker is dept-SCOPED to the registration
  // form's selected department (server-scoped, dept-tagged ∪ org-wide). Falsy
  // anchor → null → full catalog (same anchor semantics as A4's
  // RegistrationProceduresSection).
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const pickerDeptAnchor = procedurePickerDeptAnchor({ department_id: departmentId });
  const [advised, setAdvised] = useState([]);       // doctor-advised, awaiting assignment
  const [advisedSel, setAdvisedSel] = useState({}); // group key -> checked
  const [picked, setPicked] = useState([]);         // catalog_procedure_ids, in add order
  const [pick, setPick] = useState('');
  const [qtyByProcId, setQtyByProcId] = useState({}); // catalog procedure_id -> quantity (W6/C5)
  // L1 — per-catalog-row credit toggle (admission context only). Absent key ⇒
  // the IP default (ON); explicit `false` ⇒ desk switched to "Collect now".
  const [creditByProcId, setCreditByProcId] = useState({});

  useEffect(() => {
    if (!procedures.length) fetchProcedures();
  }, [procedures.length, fetchProcedures]);

  // ── Scoped catalog for the "+ Add Investigation…" picker ──
  // Keyed on the (primitive) dept anchor; store fetch is single-flight so
  // changing the department during registration can't storm requests.
  useEffect(() => {
    fetchProceduresForDept(pickerDeptAnchor);
  }, [pickerDeptAnchor, fetchProceduresForDept]);

  // ── Doctor-advised lab tests for this patient (awaiting assignment) ──
  // Only the `advised` + not-yet-assigned orders — the reception handoff. An
  // already-assigned / in-flight order is mid-workflow and must not be re-added.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!patientId) { setAdvised([]); return; }
      const r = await listOpenLabItems(patientId);
      if (cancelled) return;
      const items = (r?.success && Array.isArray(r.data)) ? r.data : [];
      setAdvised(advisedAwaitingAssignment(items));
    }
    load();
    return () => { cancelled = true; };
  }, [patientId]);

  // One row per test: repeat / multi-doctor advice for the same test collapses
  // into a single group listing each advisement (doctor + date).
  const groups = useMemo(() => groupAdvisedByTest(advised), [advised]);

  const advisedProcIds = useMemo(
    () => new Set(groups.map((g) => g.procedure_id).filter(Boolean)),
    [groups],
  );

  // Catalog tests offered in "+ Add Investigation" — receptionist-allowed
  // only for reception/nurse/etc, but the FULL active in-house lab catalog
  // for a LAB_TECHNICIAN caller (FIX-4 owner ruling: the technician is
  // trusted like a doctor advising, not limited to the front-desk-orderable
  // subset). De-duplicated against doctor-advised tests + already-picked
  // ones either way. Sourced from the dept-SCOPED picker slice (A5) — only
  // labs in the selected department (∪ org-wide) are offered for adding.
  //
  // IPD (admissionId present) — a REPEAT investigation (e.g. a second CT Head
  // on day 4 while day 1's is still pending assignment) must stay addable as
  // its own fresh, separately-billable order across a multi-day stay. Only the
  // OPD single-visit path (no admissionId) dedupes the catalog against an
  // already-advised test — there, "advised" IS the same visit's one workflow.
  const addable = useMemo(
    () => addableLabCatalog(pickerProcedures, admissionId ? [] : advisedProcIds, picked, isLabTechnicianCaller),
    [pickerProcedures, advisedProcIds, picked, admissionId, isLabTechnicianCaller],
  );

  const catalogRows = useMemo(() => {
    const byId = new Map((procedures || []).map((p) => [p.procedure_id, p]));
    return picked
      .map((id) => byId.get(id))
      .filter(Boolean)
      .map((p) => ({ ...p, quantity: qtyByProcId[p.procedure_id] || 1 }));
  }, [picked, procedures, qtyByProcId]);

  // Emit selected advised groups + picked catalog tests, source-tagged so the
  // submit can ASSIGN the primary advised order (and cancel duplicates) vs
  // ADVISE fresh catalog ones.
  useEffect(() => {
    onChange?.(buildInvestigationLines({
      groups, advisedSel, catalogRows, admissionId, ipNumber, creditByProcId,
    }));
  }, [groups, advisedSel, catalogRows, admissionId, ipNumber, creditByProcId, onChange]);

  // A paid group must never enter selection — guard the one mutator itself so
  // no path (click, keyboard, a future select-all) can tick a paid row.
  const toggleAdvised = useCallback((key, paid) => {
    if (paid) return;
    setAdvisedSel((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const add = useCallback((id) => {
    if (!id) return;
    setPicked((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setPick('');
  }, []);

  const remove = useCallback((id) => {
    setPicked((prev) => prev.filter((x) => x !== id));
    setQtyByProcId((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setCreditByProcId((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // L1 — toggle a catalog row between "On credit" (default) and "Collect now".
  const setCredit = useCallback((id, onCredit) => {
    setCreditByProcId((prev) => ({ ...prev, [id]: onCredit }));
  }, []);

  // Qty stepper (W6/C5) — default 1, editable, integer ≥1. Mirrors
  // RegistrationProceduresSection's `bumpQty`/`setQty` exactly. `bumpQty`
  // (+/- buttons) always lands on a valid integer by construction. `setQty`
  // (the typed input) rejects 0/blank/negative/non-numeric by falling back to
  // the row's current quantity — the input never goes below 1.
  const bumpQty = useCallback((id, delta) => {
    setQtyByProcId((prev) => {
      const qty = Math.max(1, (prev[id] || 1) + delta);
      return { ...prev, [id]: qty };
    });
  }, []);
  const setQty = useCallback((id, raw) => {
    setQtyByProcId((prev) => {
      const n = parseInt(raw, 10);
      const qty = Number.isInteger(n) && n >= 1 ? n : (prev[id] || 1);
      return { ...prev, [id]: qty };
    });
  }, []);

  const card = {
    borderRadius: '0.75rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#cbd5e1'}`,
    background: isDark ? '#1e293b' : '#f8fafc',
    padding: '0.875rem',
    marginTop: '0.75rem',
  };
  const chip = (bg, text) => ({
    fontSize: '0.625rem', fontWeight: 600, padding: '0.0625rem 0.375rem',
    borderRadius: '9999px', background: bg, color: text, marginLeft: '0.375rem',
  });

  return (
    <div style={card}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: '0.5rem' }}>
        🧪 Investigations for this visit
      </div>

      {/* Business fix (LAB-ASSIGN-2 FIX-3 item 1) — a technician's walk-in
          form must never leave a doctor-advised test invisible AND
          unorderable at once. Owner's no-self-claim ruling stands (a
          technician still can't tick/assign it here), so this is a READ-ONLY
          informational line naming why: no checkbox, no assign call, not
          counted in `advisedSel`, never submitted. It stays out of
          "+ Add Investigation" too (unchanged `advisedProcIds` exclusion
          below) so the front desk's eventual assign can't be duplicated. */}
      {!admissionId && isLabTechnicianCaller && groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: catalogRows.length > 0 || addable.length > 0 ? '0.5rem' : 0 }}>
          {groups.map((g) => (
            <div
              key={g.key}
              style={{ fontSize: '0.75rem', fontStyle: 'italic', color: isDark ? '#94a3b8' : '#64748b', padding: '0.25rem 0.5rem' }}
            >
              {g.test_name} — {g.advised_by_doctor_name ? `advised by Dr ${g.advised_by_doctor_name}` : 'already advised'} · the front desk assigns this to you.
            </div>
          ))}
        </div>
      )}

      {/* Doctor-advised tests (top) — one row per test, listing each advisement
          (doctor + date). Tick to bring onto this visit.
          IPD (admissionId present) — this block never renders. Ticking one
          here can never place an order (the submit filters `source:'advised'`
          out for an admission — OrderLabDialog.jsx), and it never leaves the
          list because an accounting-only org never assigns a technician
          (`advisedAwaitingAssignment`), so the rows would accumulate for the
          whole stay while doing nothing. The stay's real lab ledger is the
          Diagnostics tab. OPD (no admissionId) keeps this in full — there,
          ticking genuinely assigns the existing order. */}
      {!admissionId && !isLabTechnicianCaller && groups.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: catalogRows.length > 0 || addable.length > 0 ? '0.5rem' : 0 }}>
          {groups.map((g) => {
            const checked = !!advisedSel[g.key];
            // Collapse genuinely-identical advisements (same doctor AND same
            // date) to one line; two that differ in either still show as two.
            const dedupedAdvisements = [];
            const seen = new Set();
            for (const a of g.advisements) {
              const dupeKey = `${a.doctor || ''} ${a.date || ''}`;
              if (seen.has(dupeKey)) continue;
              seen.add(dupeKey);
              dedupedAdvisements.push(a);
            }
            return (
              <div key={g.key} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
                background: checked ? (isDark ? 'rgba(13,148,136,0.18)' : '#ccfbf1') : 'transparent',
              }}>
                {/* A paid row loses its checkbox entirely — it can never be
                    (re-)selected, by any path. Row stays visible with the
                    "Already paid" chip below; a fixed-width placeholder keeps
                    the row's text aligned with unpaid rows. */}
                {g.paid ? (
                  <span style={{ width: '0.875rem', height: '0.875rem', marginTop: '0.1875rem', flexShrink: 0 }} />
                ) : (
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAdvised(g.key, g.paid)}
                    style={{ marginTop: '0.1875rem' }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.8125rem', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                    {g.test_name}
                    {g.paid && (
                      <span
                        title="Already paid — cannot be ordered again."
                        style={chip(isDark ? 'rgba(34,197,94,0.25)' : '#dcfce7', isDark ? '#86efac' : '#166534')}
                      >
                        Already paid
                      </span>
                    )}
                  </div>
                  {/* One line per DISTINCT advisement: doctor + date it was
                      advised. Same doctor + same date collapses to one line. */}
                  {dedupedAdvisements.map((a, i) => (
                    <div key={i} style={{ fontSize: '0.6875rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                      {a.doctor ? `Dr. ${a.doctor}` : 'Doctor advised'}{a.date ? ` · ${a.date}` : ''}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: g.paid ? (isDark ? '#86efac' : '#16a34a') : (isDark ? '#f1f5f9' : '#0f172a') }}>
                  {g.paid ? 'Paid' : (g.skip_payment ? '—' : `₹${Number(g.cost || 0)}`)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Catalog (receptionist-orderable) picks — the only rows that carry a
          qty stepper (W6/C5). Doctor-advised rows above have none. */}
      {catalogRows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {catalogRows.map((r) => {
            const qty = r.quantity || 1;
            const unit = Number(r.default_cost || 0);
            // L1 — credit toggle only in an admission context, and only for a
            // billable row (a No-charge test has nothing to settle on credit).
            const showCreditToggle = !!admissionId && !r.skip_payment;
            const onCredit = creditByProcId[r.procedure_id] !== false; // default ON (D2)
            return (
              <div key={r.procedure_id} style={{
                display: 'flex', flexDirection: 'column', gap: '0.25rem',
                padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
                background: isDark ? 'rgba(139,92,246,0.16)' : '#ede9fe',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                  {r.name}
                  {r.skip_payment && <span style={chip(isDark ? 'rgba(100,116,139,0.3)' : '#e2e8f0', isDark ? '#cbd5e1' : '#475569')}>No charge</span>}
                </div>
                {/* Fixed-width, flexShrink:0 so a long test name (name div is
                    flex:1/minWidth:0) never pushes the stepper out of the row. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                  <button
                    type="button"
                    aria-label={`Decrease quantity for ${r.name}`}
                    onClick={() => bumpQty(r.procedure_id, -1)}
                    disabled={qty <= 1}
                    style={{
                      width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem', lineHeight: 1,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                      cursor: qty <= 1 ? 'not-allowed' : 'pointer', opacity: qty <= 1 ? 0.5 : 1,
                    }}
                  >−</button>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={qty}
                    aria-label={`Quantity for ${r.name}`}
                    onChange={(e) => setQty(r.procedure_id, e.target.value)}
                    style={{
                      width: '2rem', textAlign: 'center', fontSize: '0.75rem', padding: '0.125rem 0',
                      borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`Increase quantity for ${r.name}`}
                    onClick={() => bumpQty(r.procedure_id, 1)}
                    style={{
                      width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem', lineHeight: 1,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                      cursor: 'pointer',
                    }}
                  >+</button>
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                  {r.skip_payment ? '—' : (qty > 1 ? `₹${unit} × ${qty}` : `₹${unit}`)}
                </div>
                <button
                  type="button"
                  onClick={() => remove(r.procedure_id)}
                  aria-label={`Remove ${r.name}`}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    color: isDark ? '#94a3b8' : '#64748b', fontSize: '1rem', lineHeight: 1, padding: '0 0.25rem',
                  }}
                >✕</button>
              </div>
              {/* LAB-ASSIGN-2 / FE-4 — the technician's IPD lines are fixed to
                  On credit (owner ruling: role holds GRANT_CREDIT, not
                  COLLECT_PAYMENT). Static frozen line, no chooser rendered —
                  `creditByProcId` is never set for this role, so the default
                  ON (credit) carries through to every submitted line. */}
              {showCreditToggle && isLabTechnicianCaller && (
                <div style={{ paddingLeft: '0.125rem', fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b' }}>
                  On credit — settled at discharge.
                </div>
              )}
              {showCreditToggle && !isLabTechnicianCaller && (
                <div style={{ display: 'flex', gap: '0.375rem', paddingLeft: '0.125rem' }}>
                  {canCollectNow && (
                    <button
                      type="button"
                      onClick={() => setCredit(r.procedure_id, false)}
                      aria-pressed={!onCredit}
                      style={{
                        fontSize: '0.6875rem', fontWeight: 600, padding: '0.1875rem 0.5rem', borderRadius: '9999px',
                        border: `1px solid ${!onCredit ? (isDark ? '#0d9488' : '#0f766e') : (isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1')}`,
                        background: !onCredit ? (isDark ? '#0d9488' : '#0f766e') : (isDark ? '#0f172a' : '#fff'),
                        color: !onCredit ? '#fff' : (isDark ? '#f1f5f9' : '#0f172a'),
                        cursor: 'pointer',
                      }}
                    >
                      Collect payment now
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setCredit(r.procedure_id, true)}
                    aria-pressed={onCredit}
                    style={{
                      fontSize: '0.6875rem', fontWeight: 600, padding: '0.1875rem 0.5rem', borderRadius: '9999px',
                      border: `1px solid ${onCredit ? '#000' : (isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1')}`,
                      background: onCredit ? '#000' : (isDark ? '#0f172a' : '#fff'),
                      color: onCredit ? '#fff' : (isDark ? '#f1f5f9' : '#0f172a'),
                      cursor: 'pointer',
                    }}
                  >
                    On credit (settle later)
                  </button>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      {addable.length > 0 ? (
        <div style={{ marginTop: (groups.length > 0 || catalogRows.length > 0) ? '0.5rem' : 0, display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
          <select
            value={pick}
            onChange={(e) => add(e.target.value)}
            style={{
              flex: 1, fontSize: '0.75rem', padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
              background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
            }}
          >
            <option value="">+ Add Investigation…</option>
            {addable.map((p) => (
              <option key={p.procedure_id} value={p.procedure_id}>
                {p.name}{p.skip_payment ? ' — No charge' : ` — ₹${Number(p.default_cost || 0)}`}
              </option>
            ))}
          </select>
        </div>
      ) : (
        groups.length === 0 && catalogRows.length === 0 && (
          <div style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            {isLabTechnicianCaller
              ? 'No lab tests in the catalog yet — an admin adds them under Lab Catalog.'
              : 'No front-desk lab tests configured. Mark a lab test “Receptionist” in Lab Catalog (or have a doctor advise it).'}
          </div>
        )
      )}
    </div>
  );
}
